/**
 * ========================================
 * CART DRAWER - cart-drawer.js
 * ========================================
 * Improved version with better upsell handling,
 * error recovery, and loading states
 * ========================================
 */

if (!customElements.get("cart-drawer")) {

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindUpsellButton();
    
    // Track processing state
    this.isProcessing = false;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Bind upsell button clicks with improved error handling
   */
  bindUpsellButton() {
    this.addEventListener("click", (event) => {
      // Match any upsell add button (handles dynamic class names)
      const upsellButton = event.target.closest('[class*="upsell-add-button"]');
      
      if (upsellButton) {
        // Check if already being handled by onclick attribute
        const onclickAttr = upsellButton.getAttribute('onclick');
        if (onclickAttr && onclickAttr.includes('handleProductUpsellAdd')) {
          // Let the onclick handler deal with it
          return;
        }
        
        event.preventDefault();
        event.stopPropagation();

        // Prevent double-clicks
        if (this.isProcessing || upsellButton.disabled) {
          return;
        }

        const form = upsellButton.closest(".upsell-form, form");
        if (!form) {
          console.error('[CartDrawer] No form found for upsell button');
          return;
        }

        const variantInput = form.querySelector('input[name="id"]');
        if (!variantInput || !variantInput.value) {
          console.error('[CartDrawer] No variant ID found in form');
          this.showButtonError(upsellButton, window.t('Error'));
          return;
        }

        this.addItemToCart(variantInput.value, upsellButton);
      }
    });
  }

  /**
   * Show loading state on button
   */
  showButtonLoading(button) {
    if (!button) return;
    
    const textSpan = button.querySelector('[class*="upsell-button-text"]');
    if (textSpan && !button.dataset.originalHtml) {
      button.dataset.originalHtml = textSpan.innerHTML;
    }
    
    button.disabled = true;
    button.classList.add('is-loading');
    
    if (textSpan) {
      textSpan.innerHTML = '<span class="button-spinner"></span>';
    }
  }

  /**
   * Show success state on button
   */
  showButtonSuccess(button) {
    if (!button) return;
    
    const textSpan = button.querySelector('[class*="upsell-button-text"]');
    
    button.classList.remove('is-loading');
    button.classList.add('upsell-add-success');
    
    if (textSpan) {
      textSpan.innerHTML = '✓ ' + window.t('Added');
    }
  }

  /**
   * Show error state on button
   */
  showButtonError(button, message = null) {
    if (!message) message = '✗ ' + window.t('Error');
    if (!button) return;
    
    const textSpan = button.querySelector('[class*="upsell-button-text"]');
    const originalHtml = button.dataset.originalHtml;
    
    button.disabled = false;
    button.classList.remove('is-loading', 'upsell-add-success');
    button.classList.add('upsell-add-error');
    
    if (textSpan) {
      textSpan.innerHTML = message;
    }
    
    // Reset after showing error
    setTimeout(() => {
      button.classList.remove('upsell-add-error');
      if (textSpan && originalHtml) {
        textSpan.innerHTML = originalHtml;
      }
      delete button.dataset.originalHtml;
    }, 2500);
  }

  /**
   * Add item to cart with improved error handling
   */
  async addItemToCart(variantId, button = null) {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    
    if (button) {
      this.showButtonLoading(button);
    }

    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      id: parseInt(variantId, 10),
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname + (window.Shopify?.locale && window.Shopify.locale !== 'en' ? `?locale=${window.Shopify.locale}` : ''),
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body,
      });

      const parsedState = await response.json();

      if (!response.ok || response.status === 422) {
        const errorMessage = parsedState.description || parsedState.message || 'Error';
        console.error("[CartDrawer] Error adding item to cart:", errorMessage);
        
        if (button) {
          // Determine error message
          let displayError = '✗ ' + window.t('Error');
          const errorLower = errorMessage.toLowerCase();
          if (errorLower.includes('sold out') || errorLower.includes('not available')) {
            displayError = window.t('Sold out');
          } else if (errorLower.includes('quantity')) {
            displayError = window.t('Max quantity');
          }
          this.showButtonError(button, displayError);
        }
        
        this.isProcessing = false;
        return;
      }

      // Success
      if (button) {
        this.showButtonSuccess(button);
      }

      // Short delay to show success state before updating
      await new Promise(resolve => setTimeout(resolve, 300));
      
      this.renderContents(parsedState);
      
    } catch (error) {
      console.error("[CartDrawer] Network error adding item to cart:", error);
      if (button) {
        this.showButtonError(button);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  startCountdownTimer() {
    const timerElement = document.getElementById("cart-countdown-timer");
    if (!timerElement) return;

    let minutes = 10;
    let seconds = 0;

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    const updateTimer = () => {
      timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    updateTimer();

    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          return;
        }
        minutes--;
        seconds = 59;
      } else {
        seconds--;
      }
      updateTimer();
    }, 1000);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");

    // Initialize slick slider if available
    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }

    this.startCountdownTimer();
  }

  close() {
    const cartCustomizationMode = document.querySelector("cart-drawer")?.classList.contains("customization-mode");
    if (cartCustomizationMode) {
      alert("Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing.");
      return;
    }

    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner")?.classList.remove("is-empty");
    this.productId = parsedState.id;
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      
      if (section.id === "cart-drawer" || section.selector === "#CartDrawer") {
        const newCartDrawer = this.getSectionDOM(parsedState.sections[section.id], section.selector);
        if (typeof updateCartDrawer === 'function') {
          updateCartDrawer(sectionElement, newCartDrawer);
        } else {
          // Fallback
          const oldDrawerInner = sectionElement.querySelector('.drawer__inner');
          const newDrawerInner = newCartDrawer?.querySelector('.drawer__inner');
          if (oldDrawerInner && newDrawerInner) {
            oldDrawerInner.innerHTML = newDrawerInner.innerHTML;
          }
        }
      } else {
        if (section.id === "cart-icon-bubble") {
          const iconType = sectionElement.getAttribute("data-cart-icon-type");
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          if (iconType) {
            sectionElement.setAttribute("data-cart-icon-type", iconType);
          }
          // Handle icon display
          const iconWrapper = sectionElement.querySelector(".svg-wrapper");
          if (iconWrapper && iconType) {
            const bagIcon = iconWrapper.querySelector(".cart-icon-bag");
            const cartIcon = iconWrapper.querySelector(".cart-icon-cart");
            const cartIconEmpty = iconWrapper.querySelector(".cart-icon-cart-empty");
            
            if (iconType === "bag") {
              if (bagIcon) bagIcon.style.display = "flex";
            } else {
              const cartEmpty = parsedState.item_count === 0;
              if (cartEmpty && cartIconEmpty) {
                cartIconEmpty.style.display = "flex";
              } else if (cartIcon) {
                cartIcon.style.display = "flex";
              }
            }
          }
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });

    // Rebind overlay click after content update
    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
      this.open();
      
      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        bubbles: true, 
        detail: { source: 'cart-drawer', cartData: parsedState } 
      }));
    });

    // Reinitialize slick slider
    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    // Initialize slick slider after a delay
    setTimeout(() => {
      if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
        const $slider = $(".gbfrequently-bought-with-main-whole-slider");
        if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
          $slider.slick({
            infinite: true,
            autoplaySpeed: 1000,
            autoplay: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
          });
          $slider.css("opacity", "1");
        }
      }
    }, 500);

    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

} // end if (!customElements.get("cart-drawer"))

// Wait for CartItems to be defined before extending it
if (typeof CartItems !== "undefined") {
  defineCartDrawerItems();
} else {
  const checkCartItems = setInterval(() => {
    if (typeof CartItems !== "undefined") {
      clearInterval(checkCartItems);
      defineCartDrawerItems();
    }
  }, 50);
}

function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) {
    return;
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      setTimeout(() => {
        if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
          const $slider = $(".gbfrequently-bought-with-main-whole-slider");
          if ($slider.length > 0) {
            $slider.slick({
              infinite: true,
              autoplaySpeed: 1000,
              autoplay: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              dots: true,
            });
            $slider.css("opacity", "1");
          }
        }
      }, 500);

      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}

/* ====================================================================
 * Cart Items Event Delegation (variant stepper, delete, subscriptions)
 * ====================================================================
 * These handlers use document-level event delegation so they survive
 * DOM replacement. They MUST live in this always-loaded file — NOT in
 * an inline <script> inside cart-items.liquid — because inline scripts
 * don't execute when HTML is injected via DOMParser / innerHTML.
 * ==================================================================== */
(function(){
  if (window.__llCartHandlersRegistered) return;
  window.__llCartHandlersRegistered = true;

  var refreshCart = function() {
    var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + '?section_id=cart-drawer';
    fetch(url).then(function(r){return r.text();}).then(function(html){
      var oldDrawer = document.querySelector('cart-drawer');
      if (!oldDrawer) return;
      if (typeof updateCartDrawer === 'function') {
        var p = new DOMParser();
        var newDoc = p.parseFromString(html, 'text/html');
        var newCartDrawer = newDoc.querySelector('#CartDrawer');
        if (newCartDrawer) updateCartDrawer(oldDrawer, newCartDrawer, true);
      } else {
        var t=document.createElement('div');t.innerHTML=html;var n=t.querySelector('cart-drawer');if(n)oldDrawer.innerHTML=n.innerHTML;
      }
      setTimeout(updateStepperButtons, 50);
    }).catch(function(){location.reload();});
  };

  var switchPlan = function(v,k,q,p) {
    fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:k,quantity:0})})
    .then(function(){
      var b={id:parseInt(v),quantity:q};
      if(p)b.selling_plan=parseInt(p);
      return fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(b)});
    })
    .then(refreshCart)
    .catch(function(){location.reload();});
  };

  /* Subscribe button (one-time → subscription) */
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.cart-subscribe-btn');
    if(btn && !btn.classList.contains('loading')){
      btn.classList.add('loading');
      btn.textContent=window.t('Updating...');
      switchPlan(btn.dataset.variantId,btn.dataset.lineKey,parseInt(btn.dataset.quantity)||1,btn.dataset.planId);
    }
  });

  /* Native select plan switcher */
  document.addEventListener('change', function(e) {
    var sel = e.target.closest('.cart-plan-select');
    if(!sel) return;
    sel.disabled = true;
    sel.style.opacity = '0.5';
    switchPlan(sel.dataset.variantId, sel.dataset.lineKey, parseInt(sel.dataset.quantity)||1, sel.value);
  });

  /* ===== Variant Stepper Logic ===== */
  document.addEventListener('click', function(e) {
    var prev = e.target.closest('.ll-vs__prev');
    var next = e.target.closest('.ll-vs__next');
    if (!prev && !next) return;

    var stepper = (prev || next).closest('.ll-variant-stepper');
    if (!stepper || stepper.classList.contains('is-loading')) return;

    var productId = stepper.dataset.productId;
    var currentVid = parseInt(stepper.dataset.currentVariantId);
    var itemKey = stepper.dataset.itemKey;
    var sellingPlanId = stepper.dataset.sellingPlanId;
    var itemQty = parseInt(stepper.dataset.quantity) || 1;

    var dataEl = document.querySelector('.ll-variant-data[data-product-id="' + productId + '"]');
    if (!dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch(er) { return; }

    var idx = -1;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === currentVid) { idx = i; break; }
    }
    if (idx === -1) return;

    var newIdx = prev ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= variants.length) return;
    if (!variants[newIdx].available) return;

    var newVid = variants[newIdx].id;

    stepper.classList.add('is-loading');
    stepper.querySelector('.ll-vs__label').textContent = variants[newIdx].title;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 0 })
    })
    .then(function() {
      var body = { id: newVid, quantity: itemQty };
      if (sellingPlanId) body.selling_plan = parseInt(sellingPlanId);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
    })
    .then(function() { refreshCart(); })
    .catch(function() { location.reload(); });
  });

  /* Disable prev/next buttons at boundaries */
  function updateStepperButtons() {
    document.querySelectorAll('.ll-variant-stepper').forEach(function(stepper) {
      var productId = stepper.dataset.productId;
      var currentVid = parseInt(stepper.dataset.currentVariantId);
      var dataEl = document.querySelector('.ll-variant-data[data-product-id="' + productId + '"]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch(er) { return; }
      var idx = -1;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === currentVid) { idx = i; break; }
      }
      var prevBtn = stepper.querySelector('.ll-vs__prev');
      var nextBtn = stepper.querySelector('.ll-vs__next');
      if (prevBtn) prevBtn.disabled = (idx <= 0);
      if (nextBtn) nextBtn.disabled = (idx >= variants.length - 1);
    });
  }
  window.updateStepperButtons = updateStepperButtons;

  /* ===== Delete Button Handler (removes 1 unit per card) ===== */
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.ll-delete-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var lineKey = btn.dataset.lineKey;
    if (!lineKey) return;
    btn.style.opacity = '0.2';
    btn.style.pointerEvents = 'none';
    var qty = parseInt(btn.dataset.itemQty) || 1;
    var newQty = qty > 1 ? qty - 1 : 0;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: newQty })
    }).then(function(){ refreshCart(); }).catch(function(){ location.reload(); });
  });

  /* Run on initial load + after any cart:updated event */
  updateStepperButtons();
  document.addEventListener('cart:updated', function() {
    setTimeout(updateStepperButtons, 100);
  });
})();